import React, { useState, useEffect } from 'react';
import { Rating, Menu, MenuItem, IconButton } from '@mui/material';
import { Star, MoreVert } from '@mui/icons-material';
import { apiClient } from '../config/ApiClient';
import '../styles/ProductReviews.css';
import { showError, showSuccess } from './Alert';

interface Review {
  reviewId: string;
  itemId: string;
  userName: string;
  displayName: string;
  rating: number;
  fitScore: number;
  comment: string;
  createdAt: string;
  updatedAt: string;
}

interface ProductReviewsProps {
  productId: string;
}

interface NewReview {
  rating: number;
  fitScore: number;
  comment: string;
}

const SizeFitBar: React.FC<{ sizeFit: number }> = ({ sizeFit }) => (
  <div className="size-fit-container">
    <span className="size-label">Runs Small</span>
    <div className="size-fit-bar">
      <div
        className="size-fit-indicator"
        style={{ left: `${Math.max(0, Math.min(100, (sizeFit - 1) * 25))}%` }}
      />
    </div>
    <span className="size-label">Runs Large</span>
  </div>
);

const SizeFitSelector: React.FC<{
  value: number;
  onChange: (value: number) => void;
}> = ({ value, onChange }) => (
  <div className="size-fit-input">
    <h4>Fit</h4>
    <div className="size-fit-container">
      <span className="size-label">Runs Small</span>
      <div
        className="size-fit-bar clickable"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percentage = x / rect.width;
          const newValue = 1 + Math.round(percentage * 4);
          onChange(newValue);
        }}
      >
        <div
          className="size-fit-indicator"
          style={{ left: `${(value - 1) * 25}%` }}
        />
      </div>
      <span className="size-label">Runs Large</span>
    </div>
  </div>
);

const ProductReviews: React.FC<ProductReviewsProps> = ({ productId }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [userReview, setUserReview] = useState<Review | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [newReview, setNewReview] = useState<NewReview>({
    rating: 0,
    fitScore: 3,
    comment: '',
  });
  const [editing, setEditing] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuReviewId, setMenuReviewId] = useState<string | null>(null);

  const fetchReviews = async () => {
    try {
      const res = await apiClient.get<Review[]>(
        `/item-review/item/${productId}`
      );
      const all = res.data;
      const sorted = all.sort((a, b) => (a.rating > b.rating ? 1 : -1));
      const _userName = localStorage.getItem('userName');
      setUserId(_userName ? _userName : '');
      const existing = sorted.find((r) => r.userName === userId);

      console.log(existing);
      setUserReview(existing || null);
      setReviews(
        existing
          ? [existing, ...sorted.filter((r) => r.userName !== userId)]
          : sorted
      );
    } catch (e) {
      showError('Could not fetch reviews');
    }
  };

  useEffect(() => {
    const _userName = localStorage.getItem('userName');
    if (_userName) {
      setUserId(_userName);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchReviews();
    }
  }, [userId, productId]);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editing && userReview) {
        await apiClient.put(`/item-review`, {
          reviewId: userReview.reviewId,
          rating: newReview.rating,
          fitScore: newReview.fitScore,
          comment: newReview.comment,
        });
        showSuccess('Review updated');
        fetchReviews();
        setEditing(false);
      } else {
        await apiClient.post('/item-review', {
          itemId: productId,
          rating: newReview.rating,
          fitScore: newReview.fitScore,
          comment: newReview.comment,
        });
        showSuccess('Review submitted');
      }
      setNewReview({ rating: 0, fitScore: 3, comment: '' });
      fetchReviews();
    } catch (err) {
      showError('Failed to submit review');
    }
  };

  const deleteReview = async (id: string) => {
    try {
      await apiClient.delete(`/item-review/${id}`);
      showSuccess('Review deleted');
      setUserReview(null);
      setNewReview({ rating: 0, fitScore: 3, comment: '' });
      // setEditing(false);
      fetchReviews();
    } catch {
      showError('Failed to delete review');
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    reviewId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuReviewId(reviewId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuReviewId(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    if (userReview) {
      setEditing(true);
      setNewReview({
        rating: userReview.rating,
        fitScore: userReview.fitScore,
        comment: userReview.comment,
      });
    }
  };

  const handleDelete = () => {
    handleMenuClose();
    if (menuReviewId) deleteReview(menuReviewId);
  };

  const avgRating = reviews.length
    ? reviews.reduce((a, b) => a + b.rating, 0) / reviews.length
    : 0;
  const avgFit = reviews.length
    ? reviews.reduce((a, b) => a + b.fitScore, 0) / reviews.length
    : 3;

  return (
    <div className="reviews-section">
      <h3>Customer Reviews</h3>
      <div className="average-scores">
        <div className="average-rating">
          <strong>Average Rating:</strong>
          <Rating value={avgRating} precision={0.1} readOnly />
          <span>{avgRating.toFixed(1)}</span>
        </div>
        <SizeFitBar sizeFit={avgFit} />
      </div>

      {(!userReview || editing) && (
        <form onSubmit={handleSubmitReview} className="review-form">
          <h4>{editing ? 'Update Review' : 'Write a Review'}</h4>
          <Rating
            value={newReview.rating}
            onChange={(_, value) =>
              setNewReview((prev) => ({ ...prev, rating: value || 0 }))
            }
            precision={1}
            icon={<Star fontSize="large" />}
          />
          <SizeFitSelector
            value={newReview.fitScore}
            onChange={(value) =>
              setNewReview((prev) => ({ ...prev, fitScore: value }))
            }
          />
          <textarea
            value={newReview.comment}
            onChange={(e) =>
              setNewReview((prev) => ({ ...prev, comment: e.target.value }))
            }
            placeholder="Share your thoughts..."
            required
          />
          <button type="submit" className="submit-review">
            {editing ? 'Update' : 'Submit'} Review
          </button>
        </form>
      )}

      <div className="reviews-list">
        {reviews.map((review) => (
          <div key={review.reviewId} className="review-item">
            <div className="review-header">
              <Rating value={review.rating} readOnly />
              <span className="review-display-name">{review.displayName}</span>
              <span className="review-date">
                {new Date(review.createdAt).toLocaleDateString()}
              </span>
              {review.userName === userId && (
                <IconButton
                  onClick={(e) => handleMenuClick(e, review.reviewId)}
                >
                  <MoreVert />
                </IconButton>
              )}
            </div>
            <SizeFitBar sizeFit={review.fitScore} />
            <p className="review-comment">{review.comment}</p>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleEdit}>Edit</MenuItem>
              <MenuItem onClick={handleDelete}>Delete</MenuItem>
            </Menu>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductReviews;
